import { Injectable, Inject, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable, of, fromEvent, merge, defer } from 'rxjs';
import { map, take, shareReplay } from 'rxjs/operators';
import { CreditCard } from '@app/models';
import { TranslateService } from '@ngx-translate/core';
import { NotyService } from './noty.service';
import { environment } from '@env/environment';

declare global {
  interface Window {
    Iugu?: any;
  }
}

@Injectable({ providedIn: 'root' })
export class IuguService {
  private readonly scriptId = 'iugu-v2-script';
  private readonly src = 'https://js.iugu.com/v2';
  private loading$?: Observable<any>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private translateService: TranslateService,
    private notyService: NotyService,
    private ngZone: NgZone
  ) {}

  load(): Observable<any> {
    if ((window as any).Iugu) {
      return of((window as any).Iugu);
    }

    const existing = this.document.getElementById(this.scriptId) as HTMLScriptElement | null;
    if (existing && (window as any).Iugu) {
      return of((window as any).Iugu);
    }

    if (!this.loading$) {
      this.loading$ = defer(() => {
        const already = this.document.getElementById(this.scriptId);
        const script = (already as HTMLScriptElement) ?? this.createScript();

        const loaded$ = fromEvent(script, 'load').pipe(
          map(() => {
            const Iugu = (window as any).Iugu;
            if (Iugu) {
              Iugu.setAccountID(environment.iuguID);
              Iugu.setTestMode(!environment.production);
            }
            return Iugu;
          })
        );

        const error$ = fromEvent(script, 'error').pipe(
          map(() => {
            throw new Error('Falha ao carregar Iugu JS.');
          })
        );

        return merge(loaded$, error$).pipe(take(1));
      }).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    }

    return this.loading$;
  }

  unload(): void {
    const el = this.document.getElementById(this.scriptId);
    if (el?.parentNode) el.parentNode.removeChild(el);
    delete (window as any).Iugu;
    this.loading$ = undefined;
  }

  isLoaded(): boolean {
    return typeof (window as any).Iugu !== 'undefined';
  }

  createPaymentToken(card: Partial<CreditCard>): Observable<string> {
    return new Observable<string>(subscriber => {
      this.load()
        .pipe(take(1))
        .subscribe({
          next: (Iugu: any) => {
            if (!card.expiration) {
              this.translateService.instant('settings.invalid_expiration_date');
              return;
            }

            const month = card.expiration.substring(0, 2);
            const year = card.expiration.substring(2, 4);
            const first = card.full_name[0];
            // const last = card.full_name[1];

            const cc = new Iugu.CreditCard(card.number, month, year, first, card.full_name, card.verification_value);

            Iugu.createPaymentToken(cc, (response: any) => {
              this.ngZone.run(() => {
                if (response?.errors) {
                  this.handleIuguErrors(response.errors);
                  subscriber.complete();
                } else {
                  subscriber.next(response.id);
                  subscriber.complete();
                }
              });
            });
          },
          error: err => subscriber.error(err)
        });
    });
  }

  private handleIuguErrors(errors: any): void {
    const errorMap: Record<string, string> = {
      number: 'settings.invalid_card_number',
      verification_value: 'settings.invalid_cvv',
      expiration: 'settings.invalid_expiration_date',
      first_name: 'settings.invalid_first_name',
      last_name: 'settings.invalid_last_name',
      full_name: 'settings.invalid_full_name'
    };

    for (const [field, details] of Object.entries(errors)) {
      const key = errorMap[field] || 'settings.generic_error';
      const msg = this.translateService.instant(key);
      this.notyService.error(msg);
    }
  }

  private createScript(): HTMLScriptElement {
    const script = this.document.createElement('script');
    script.id = this.scriptId;
    script.type = 'text/javascript';
    script.async = true;
    script.src = this.src;
    this.document.getElementsByTagName('head')[0].appendChild(script);
    return script;
  }
}
